<template>
  <div class="data-entry">
    <h2>Внесение данных</h2>
    <el-container class="data-entry-content">
      <el-aside
        :width="(collapsed ? 0 : asideWidth) + 'px'"
        class="side-panel"
        :class="{ 'is-resizing': resizing }"
      >
        <!-- Ширина дерева не меняется при сворачивании: панель уезжает, а не сминается -->
        <div class="side-panel__inner" :style="{ width: asideWidth + 'px' }">
          <org-tree-panel
            ref="orgTree"
            @select="onSelectUnit"
            @loaded="restoreSelected"
          ></org-tree-panel>
        </div>
      </el-aside>
      <!-- Перетаскиваемая граница левой панели, двойной клик - ширина по умолчанию -->
      <div
        v-if="!collapsed"
        class="side-resizer"
        :class="{ 'is-dragging': resizing }"
        title="Потяните, чтобы изменить ширину. Двойной клик - исходная ширина"
        @mousedown.prevent="startResize"
        @dblclick="setAsideWidth(DEFAULT_ASIDE_WIDTH)"
      ></div>
      <!-- Свёрнутая панель: закладка, за которую её вытягивают обратно (или нажатием) -->
      <div
        v-else
        class="side-tab"
        title="Потяните или нажмите, чтобы показать подразделения и отходы"
        @mousedown.prevent="startResize"
      >
        <i class="el-icon-d-arrow-right"></i>
        <span class="side-tab__label">Подразделения и отходы</span>
      </div>
      <el-main>
        <waste-entry
          v-if="selectedUnit && selectedUnit.isWaste"
          ref="entry"
          :key="entryKey"
          :waste="selectedUnit"
          :unit-path="selectedPath"
          class="wizard"
          @saved="entryKey++"
          @cancel="entryKey++"
        ></waste-entry>
        <el-empty
          v-else
          description="Раскройте подразделение слева и выберите отход"
        ></el-empty>
      </el-main>
    </el-container>
  </div>
</template>

<script>
import OrgTreePanel from "@/components/ecologist/dataEntryNew/orgTreePanel";
import WasteEntry from "@/components/ecologist/dataEntryNew/wasteEntry";

const DEFAULT_ASIDE_WIDTH = 360;
const MIN_ASIDE_WIDTH = 240;
const ASIDE_WIDTH_KEY = "dataEntryNew.asideWidth";
// Последний выбранный отход: при возвращении на страницу открывается снова
const SELECTED_KEY = "dataEntryNew.selected";

export default {
  name: "dataEntryNewMain",
  components: { OrgTreePanel, WasteEntry },
  data() {
    return {
      selectedUnit: null,
      // Подразделения над выбранным отходом - для подписи над этапом
      selectedPath: [],
      DEFAULT_ASIDE_WIDTH,
      asideWidth: DEFAULT_ASIDE_WIDTH,
      resizing: false,
      // Левая панель сужена в ноль: сворачивается при выборе режима внесения
      collapsed: false,
      // Смена ключа пересоздаёт мастер: при выборе другого отхода и после сохранения
      entryKey: 0,
    };
  },

  computed: {
    entryMode() {
      return this.$store.state.wasteEntry.mode;
    },
  },

  watch: {
    // Выбрали режим - место под внесение; начали заново (сохранили, отменили) - дерево снова видно
    entryMode(mode) {
      this.collapsed = !!mode;
    },
  },

  methods: {
    // Не шире 60% экрана внесения данных, чтобы мастеру оставалось место
    setAsideWidth(width) {
      const container = this.$el.querySelector(".data-entry-content");
      const max = Math.max(MIN_ASIDE_WIDTH, container.clientWidth * 0.6);
      this.asideWidth = Math.round(
        Math.min(max, Math.max(MIN_ASIDE_WIDTH, width))
      );
      try {
        localStorage.setItem(ASIDE_WIDTH_KEY, this.asideWidth);
      } catch (e) {
        // ширина просто не запомнится
      }
    },

    /*
      Тянуть границу или закладку: ширина идёт за мышью, уже половины минимальной - панель
      сужается в ноль. Нажатие на закладку без перетаскивания разворачивает панель
    */
    startResize(event) {
      const startX = event.clientX;
      const startWidth = this.collapsed ? 0 : this.asideWidth;
      let moved = false;
      this.resizing = true;
      // Курсор и запрет выделения текста на всей странице, пока тянем
      document.body.classList.add("is-col-resizing");
      const move = (e) => {
        const width = startWidth + e.clientX - startX;
        if (Math.abs(e.clientX - startX) > 3) moved = true;
        this.collapsed = width < MIN_ASIDE_WIDTH / 2;
        if (!this.collapsed) this.setAsideWidth(width);
      };
      const stop = () => {
        if (!moved && startWidth === 0) this.collapsed = false;
        this.resizing = false;
        document.body.classList.remove("is-col-resizing");
        window.removeEventListener("mousemove", move);
        window.removeEventListener("mouseup", stop);
      };
      window.addEventListener("mousemove", move);
      window.addEventListener("mouseup", stop);
    },

    async onSelectUnit(unit, path = []) {
      const entry = this.$refs.entry;
      if (entry && entry.isDirty) {
        try {
          await this.confirm(
            "Внимание",
            "Введённые данные не сохранены и будут потеряны. Перейти к другому элементу?",
            "warning",
            "Да",
            "Нет"
          );
        } catch {
          this.$refs.orgTree.setCurrent(
            this.selectedUnit ? this.selectedUnit.key : null
          );
          return;
        }
      }
      this.selectedUnit = unit;
      this.selectedPath = path;
      this.entryKey++;
      try {
        localStorage.setItem(SELECTED_KEY, unit.key);
      } catch (e) {
        // выбор просто не запомнится
      }
    },

    restoreSelected() {
      let key = null;
      try {
        key = localStorage.getItem(SELECTED_KEY);
      } catch (e) {
        // хранилище недоступно - ничего не выбрано
      }
      if (key && !this.$refs.orgTree.selectByKey(key)) {
        try {
          localStorage.removeItem(SELECTED_KEY);
        } catch (e) {
          // не страшно
        }
      }
    },
  },

  mounted() {
    let saved = null;
    try {
      saved = Number(localStorage.getItem(ASIDE_WIDTH_KEY));
    } catch (e) {
      // хранилище недоступно - остаётся ширина по умолчанию
    }
    if (saved) this.setAsideWidth(saved);
  },
};
</script>

<style scoped>
/*
  Корень - обычный блок, а не flex: flex-контейнер сужался плавающей кнопкой «Назад»
  из containerComponent на всю высоту (сейчас на этой странице она скрыта, meta.hideBack).
  Заголовок по высоте равен кнопке, поэтому контейнер ниже занимал бы всю ширину и с ней.
*/
.data-entry {
  height: var(--content-min-height, 100vh);
}
.data-entry > h2 {
  margin: 0 0 16px;
  line-height: 40px;
}
.data-entry-content {
  height: calc(100% - 56px);
}
/* Сворачивается плавно, а за мышью при перетягивании - без задержки */
.side-panel {
  overflow: hidden;
  transition: width 0.25s ease-in-out;
}
.side-panel.is-resizing {
  transition: none;
}
.side-panel__inner {
  height: 100%;
  box-sizing: border-box;
  padding-right: 5px;
}
/* Закладка свёрнутой панели - в рамке, как блоки справа, прилегает к левому краю */
.side-tab {
  flex: 0 0 auto;
  align-self: flex-start;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 12px 4px;
  border: 1.5px solid #dcdfe6;
  border-left: none;
  border-radius: 0 8px 8px 0;
  background: #fff;
  color: #606266;
  font-size: 13px;
  cursor: col-resize;
  user-select: none;
  transition: color 0.15s, border-color 0.15s;
}
.side-tab:hover {
  color: #409eff;
  border-color: #409eff;
}
.side-tab__label {
  writing-mode: vertical-rl;
  transform: rotate(180deg);
}
/* Полоса 7px для захвата мышью, линия по центру видна только при наведении: у панели своя рамка */
.side-resizer {
  flex: 0 0 7px;
  margin: 0 -3px;
  position: relative;
  z-index: 1;
  cursor: col-resize;
}
.side-resizer::before {
  content: "";
  position: absolute;
  top: 0;
  bottom: 0;
  left: 2px;
  /*
    Линия - рамкой, а не фоном: рамку браузер выравнивает по пикселям экрана,
    и при масштабе больше 100% она той же толщины, что и рамки блоков справа
  */
  border-left: 1.5px solid transparent;
  transition: border-color 0.15s, border-width 0.15s, left 0.15s;
}
.side-resizer:hover::before,
.side-resizer.is-dragging::before {
  left: 1px;
  border-left-width: 4px;
  border-left-color: #409eff;
}
.el-main {
  display: flex;
  flex-direction: column;
  padding-top: 0;
  padding-bottom: 0;
  /* Иначе поднятая в строку заголовка линия этапов обрезается; прокрутка - внутри этапа */
  overflow: visible;
  /* Без overflow: auto flex-элемент растягивается под широкую таблицу - запрещаем */
  min-width: 0;
}
.wizard {
  flex: 1;
  min-height: 0;
}
/*
  Линия этапов поднята в строку заголовка, справа от названия (кнопка «Назад» здесь скрыта).
  Высота линии фиксированная и равна сдвигу вверх, так что места под заголовком она не занимает:
  заголовок с отступом под ним (40 + 16) и ещё 4px на глаз
*/
.wizard ::v-deep .step-line {
  --step-line-height: 60px;
  height: var(--step-line-height);
  margin-top: calc(-1 * var(--step-line-height));
}
</style>

<style>
body.is-col-resizing {
  cursor: col-resize;
  user-select: none;
}
</style>
